import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/utils/api';

export interface MaintenanceStatus {
    active: boolean;
    allowed: boolean;
    site_name?: string | null;
    logo_url?: string | null;
    tagline?: string | null;
    maintenance_message?: string | null;
}

/**
 * Polls the uncached maintenance-status endpoint. Fetches immediately on
 * mount, then re-fetches every 2 s while maintenance is active and the
 * current visitor is not allowed (admin or allowlisted). This is the
 * cache-latency solution: the server-side gate caches for 5 s, but the
 * client sees toggles within one poll tick.
 */
export function useMaintenanceStatus(): { status: MaintenanceStatus | null; refresh: () => void } {
    const [status, setStatus] = useState<MaintenanceStatus | null>(null);
    const inFlight = useRef(false);

    const fetchStatus = useCallback(async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        try {
            const response = await api.get('/v1/public/maintenance-status');
            setStatus(response.data as MaintenanceStatus);
        } catch {
            // Non-fatal: keep the last known status (or null) if the endpoint is unavailable.
        } finally {
            inFlight.current = false;
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // Poll every 2 s only while locked out (active && !allowed).
    useEffect(() => {
        if (!status?.active || status.allowed) return;
        const interval = setInterval(fetchStatus, 2000);
        return () => clearInterval(interval);
    }, [status, fetchStatus]);

    return { status, refresh: fetchStatus };
}
